import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useOAuth } from '../../contexts/OAuthContext';
import { useToast } from '../Toast';
import { getProviders } from '../../services/oauthApi';
import { 
  X, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  RefreshCw,
  ExternalLink,
  Zap,
  Shield
} from 'lucide-react';

// Provider Logo component
const ProviderLogo = ({ logoUrl, brandColor, className = "w-8 h-8", fallbackName }) => {
  const [imageError, setImageError] = useState(false);
  
  if (logoUrl && !imageError) {
    return (
      <img 
        src={logoUrl} 
        alt={`${fallbackName} logo`}
        className={`${className} object-contain`}
        onError={() => setImageError(true)}
      />
    );
  }
  
  // Fallback: First letter of provider name
  return (
    <div 
      className={`${className} rounded-lg flex items-center justify-center font-bold text-white text-lg`}
      style={{ backgroundColor: brandColor || '#6b7280' }}
    >
      {fallbackName?.charAt(0).toUpperCase() || '?'}
    </div>
  );
};

// Default provider info
const DEFAULT_PROVIDERS = [
  {
    name: 'quickbooks',
    display_name: 'QuickBooks',
    description: 'Sync financial data, invoices, and accounting information.',
    brand_color: '#2CA01C',
    logo_url: null,
    is_configured: true,
  },
  {
    name: 'hubspot',
    display_name: 'HubSpot',
    description: 'Sync CRM contacts, data, and information.',
    brand_color: '#FF7A59',
    logo_url: null,
    is_configured: true,
  },
];

// Helper to convert hex to rgba
const hexToRgba = (hex, alpha = 0.1) => {
  if (!hex) return 'rgba(107, 114, 128, 0.1)';
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const IntegrationsModal = ({ isOpen, onClose }) => {
  const { isDarkMode } = useDarkMode();
  const { 
    connections, 
    isLoading, 
    isConnecting, 
    initiateOAuth, 
    disconnectProvider,
    refreshConnections
  } = useOAuth();
  const toast = useToast();
  const modalRef = useRef(null);

  // State
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState(null);

  // Fetch providers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  const fetchProviders = async () => {
    try {
      setProvidersLoading(true);
      const data = await getProviders();
      setProviders(data?.length > 0 ? data : DEFAULT_PROVIDERS);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders(DEFAULT_PROVIDERS);
    } finally {
      setProvidersLoading(false);
    }
  };

  // Handle connect
  const handleConnect = async (provider) => {
    setConnectingProvider(provider.name);
    const result = await initiateOAuth(provider.name);
    setConnectingProvider(null);

    if (result.success) {
      toast.success(`Connected to ${provider.display_name}!`);
    } else if (!result.cancelled) {
      toast.error(result.message || 'Connection failed');
    }
  };

  // Handle disconnect
  const handleDisconnect = async (provider) => {
    setDisconnectingProvider(provider.name);
    const result = await disconnectProvider(provider.name);
    setDisconnectingProvider(null);

    if (result.success) {
      toast.success(`Disconnected from ${provider.display_name}`);
    } else {
      toast.error(result.message || 'Failed to disconnect');
    }
  };

  // Get connection for provider
  const getConnectionForProvider = (providerName) => {
    return connections.find(c => c.provider?.toLowerCase() === providerName?.toLowerCase());
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{
          backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          border: isDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid #e2e8f0'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          {/* Background gradient accent */}
          <div 
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)' }}
          />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                <Zap className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Integrations
                </h2>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Connect your favorite apps
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-all ${
                isDarkMode 
                  ? 'hover:bg-slate-800 text-slate-400 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
          {/* Loading state */}
          {(providersLoading || isLoading) && providers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <p className={`mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                Loading integrations...
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Refresh button */}
              <div className="flex justify-end">
                <button
                  onClick={refreshConnections}
                  disabled={isLoading}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-all ${
                    isDarkMode
                      ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {/* Provider cards */}
              {providers.map((provider) => {
                const connection = getConnectionForProvider(provider.name);
                const isConnected = connection?.status === 'active';
                const isThisConnecting = connectingProvider === provider.name;
                const isThisDisconnecting = disconnectingProvider === provider.name;
                const isConfigured = provider.is_configured !== false;

                return (
                  <div
                    key={provider.name}
                    className={`relative rounded-xl border transition-all duration-200 overflow-hidden ${
                      isConnected
                        ? isDarkMode
                          ? 'bg-emerald-500/5 border-emerald-500/30'
                          : 'bg-emerald-50/50 border-emerald-200'
                        : isDarkMode
                          ? 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Connected indicator bar */}
                    {isConnected && (
                      <div 
                        className="absolute top-0 left-0 right-0 h-0.5"
                        style={{ backgroundColor: provider.brand_color || '#10b981' }}
                      />
                    )}

                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Logo */}
                        <div 
                          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: hexToRgba(provider.brand_color, 0.12) }}
                        >
                          <ProviderLogo 
                            logoUrl={provider.logo_url}
                            brandColor={provider.brand_color}
                            fallbackName={provider.display_name}
                            className="w-7 h-7"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {provider.display_name}
                            </h3>
                            {isConnected && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
                                <CheckCircle className="w-3 h-3" />
                                Connected
                              </span>
                            )}
                            {!isConfigured && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'
                              }`}>
                                Coming soon
                              </span>
                            )}
                          </div>
                          <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                            {provider.description}
                          </p>

                          {/* Connection info */}
                          {isConnected && connection?.connected_at && (
                            <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                              Connected {new Date(connection.connected_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        {/* Action button */}
                        <div className="flex-shrink-0">
                          {isConnected ? (
                            <button
                              onClick={() => handleDisconnect(provider)}
                              disabled={isThisDisconnecting}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                isDarkMode
                                  ? 'text-red-400 hover:bg-red-500/10 disabled:opacity-50'
                                  : 'text-red-600 hover:bg-red-50 disabled:opacity-50'
                              }`}
                            >
                              {isThisDisconnecting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Disconnect'
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleConnect(provider)}
                              disabled={!isConfigured || isThisConnecting || isConnecting}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                isDarkMode
                                  ? 'bg-white text-slate-900 hover:bg-gray-100'
                                  : 'bg-slate-900 text-white hover:bg-slate-800'
                              }`}
                            >
                              {isThisConnecting ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Connecting...
                                </>
                              ) : (
                                <>
                                  Connect
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {providers.length === 0 && !providersLoading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className={`w-10 h-10 mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    No integrations available
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className={`px-6 py-4 border-t ${isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-gray-100 bg-gray-50/50'}`}
        >
          <div className="flex items-center justify-center gap-2">
            <Shield className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Your data is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

IntegrationsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IntegrationsModal;